import React, { useCallback, useMemo } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  InlineStack,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { ArrowDownIcon, ArrowUpIcon, DeleteIcon, PlusIcon } from '@shopify/polaris-icons';

import PremiumFeatureLock from './PremiumFeatureLock';

/**
 * TierList - the ordered goal ladder a merchant builds in the bar editor.
 *
 * Row 1 is always present and is never a stored tier row: on the backend it IS
 * `free_shipping_threshold` / `progress_message`, synthesised back into element
 * 0 of the list by `ShippingBar.tier_list()`. So row 1 here carries no reward
 * type and no reward label of its own - the server would drop them - and rows
 * 2..5 are the ones that become `bar_tiers` rows.
 *
 * The pure helpers below are exported because the editor needs them too: it
 * maps the loaded bar into rows, validates before saving, builds the save
 * payload, and builds the list it hands to `BarPreview`. Keeping all four in
 * one module is what stops the editor and this component from disagreeing
 * about what a tier is.
 */

// Matches MAX_TIERS in backend/app/services/bar_service.py.
export const MAX_TIERS = 5;

// Matches TIER_ONE_REWARD_LABEL in backend/app/services/bar_service.py. The
// storefront payload labels tier 1 "Free shipping" when the merchant has not
// (and they cannot - tier 1 has no label field), so `${earned}` names it. The
// admin payload carries null, which is why the preview list is built here
// rather than passed straight through.
export const TIER_ONE_REWARD_LABEL = 'Free shipping';

export const MAX_REWARD_LABEL_LENGTH = 60;
export const MAX_TIER_MESSAGE_LENGTH = 500;

// Position 1 is pinned, and this is the sentence that says so at the two
// reorder buttons it switches off. The merchant who reaches for those buttons
// is trying to make the gift the cheapest goal, and the schema cannot express
// that: tier 1 IS `free_shipping_threshold`, thresholds must ascend, so free
// shipping is always the cheapest goal on the ladder. See `moveTierRow`.
export const TIER_ONE_PINNED_REASON =
  'Tier 1 is always your free shipping goal, so it stays the cheapest goal on the ladder. Reorder the tiers above it instead.';

// Mirrors RewardType in backend/app/models.py. Display metadata only - the
// merchant sets the actual reward up in Shopify (MT-7).
export const REWARD_TYPE_OPTIONS = [
  { label: 'Free shipping', value: 'free_shipping' },
  { label: 'Free gift', value: 'free_gift' },
  { label: 'Discount', value: 'discount' },
  { label: 'Other reward', value: 'other' },
];

// eslint-disable-next-line no-template-curly-in-string
export const PLACEHOLDER_EARNED = '${earned}';
// eslint-disable-next-line no-template-curly-in-string
export const PLACEHOLDER_REMAINING = '${remaining}';
// eslint-disable-next-line no-template-curly-in-string
const NEW_TIER_MESSAGE = '🎁 Add ${remaining} more to unlock your next reward!';

/**
 * Read a threshold the merchant is typing. Returns null for anything that is
 * not a number, including the empty string - `Number('')` is 0, which would
 * silently turn a half-typed field into a free bar.
 */
export function toThresholdNumber(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Tier 1 never carries reward metadata, whatever was in the row before it got
 * there. Position 1 is not a `bar_tiers` row at all, it IS
 * `free_shipping_threshold` / `progress_message`, so it has no `reward_type`,
 * no `reward_label` and no `id` to keep - `tier_list()` always hands element 0
 * back as `id: null`.
 *
 * This normalises what arrives, it does **not** license throwing merchant
 * input away. Nothing in the editor moves a populated reward into position 1
 * any more: `moveTierRow` refuses that move outright rather than stripping the
 * reward on the way past (R10). What is left here is the loader's case, where
 * a payload can legitimately carry stale metadata on element 0 that the server
 * would never have stored.
 */
function normalizeFirstRow(rows) {
  if (rows.length === 0) return rows;
  const [first, ...rest] = rows;
  return [
    { ...first, id: null, reward_type: 'free_shipping', reward_label: '' },
    ...rest,
  ];
}

/**
 * Map a loaded bar payload into editable rows.
 *
 * `tiers` arrives whole or empty. Empty means a single-threshold bar, so row 1
 * is read off `free_shipping_threshold` / `progress_message` instead. Thresholds
 * become strings because they are what a text field holds: keeping them numeric
 * makes "10." unrepresentable and eats the decimal point as it is typed.
 */
export function tierRowsFromBar(bar) {
  const stored = Array.isArray(bar?.tiers) ? bar.tiers : [];

  if (stored.length > 1) {
    return normalizeFirstRow(
      stored.map((tier) => ({
        id: tier.id ?? null,
        threshold: tier.threshold == null ? '' : String(tier.threshold),
        reward_type: tier.reward_type || 'free_shipping',
        reward_label: tier.reward_label || '',
        message: tier.message == null ? '' : String(tier.message),
        is_enabled: tier.is_enabled !== false,
      }))
    );
  }

  return [
    {
      id: null,
      threshold:
        bar?.free_shipping_threshold == null ? '' : String(bar.free_shipping_threshold),
      reward_type: 'free_shipping',
      reward_label: '',
      message: bar?.progress_message || '',
      is_enabled: true,
    },
  ];
}

/** A fresh row, laddered above the last threshold so it validates on sight. */
export function createTierRow(rows = []) {
  const thresholds = rows
    .map((row) => toThresholdNumber(row.threshold))
    .filter((value) => value !== null);

  const first = thresholds[0];
  const last = thresholds[thresholds.length - 1];
  const stepUp = first && first > 0 ? first : 50;
  const suggested = last == null ? '' : String(Math.round((last + stepUp) * 100) / 100);

  return {
    id: null,
    threshold: suggested,
    reward_type: 'free_gift',
    reward_label: '',
    message: NEW_TIER_MESSAGE,
    is_enabled: true,
  };
}

/**
 * Whether a move is one the ladder can actually express.
 *
 * Exported so the buttons and the operation behind them cannot disagree about
 * it. The component asks this rather than re-deriving `index <= 1` next to the
 * arrows, because a control and its helper drifting apart is precisely how R7
 * shipped.
 *
 * Two rows are unmovable, for two different reasons. The ends of the list are
 * self-evident. **Position 1 is pinned**, and that one needs saying out loud -
 * see `moveTierRow`.
 */
export function canMoveTierRow(rows, index, direction) {
  const list = Array.isArray(rows) ? rows : [];
  const target = index + direction;

  if (index < 0 || index >= list.length) return false;
  if (target < 0 || target >= list.length) return false;

  return index !== 0 && target !== 0;
}

/**
 * Move a reward up or down the ladder - S-8, reordering without retyping.
 *
 * Thresholds belong to *positions*, not to rows. What a merchant means by
 * "move up" is "put this reward at the cheaper price point", never "make my
 * ladder descend" - so the two rows trade everything **except** `threshold`,
 * which stays where it is. That keeps the list ascending by construction, so a
 * reorder can never be the thing that fails `validateTiers`.
 *
 * Swapping whole rows here instead inverted the adjacent pair on every single
 * move, so Move up / Move down always produced a descending list, raised the
 * inline ascending error and blocked the save until the merchant retyped both
 * thresholds by hand - which is exactly what S-8 promises they never have to
 * do. `TierList.test.js` composes this with `validateTiers` for that reason:
 * the two were only ever tested apart, which is how it shipped.
 *
 * Excluding one named field rather than listing the ones that travel is
 * deliberate - a field added to a row later moves with its reward by default,
 * which is right for everything except a price point.
 *
 * **A move across position 1 is refused, not performed (R10.)** It reads like
 * the natural way to make the gift the cheapest goal, and the ladder cannot
 * mean that: tier 1 IS `free_shipping_threshold` / `progress_message`, so it
 * has nowhere to put a `reward_type` or a `reward_label` - `_apply_tiers`
 * reads only `threshold` and `message` off element 0, and `tier_list()`
 * hard-codes element 0 back as free shipping with a null label. Thresholds
 * ascend, element 0 is the cheapest, and element 0 is always free shipping,
 * so "gift cheapest" is unreachable by construction rather than merely
 * unimplemented.
 *
 * Performing it anyway did not relocate the reward, it destroyed it: the
 * promoted row was stripped to free shipping with a blank label and the
 * demoted row inherited tier 1's blank label, leaving a ladder with the gift
 * at no price point at all and only the two messages traded. So there was no
 * capability here to preserve behind a confirmation - only a button that could
 * not do what it offered. Refusing it keeps the merchant's reward, and
 * `TIER_ONE_PINNED_REASON` is rendered on the disabled arrows so the rule is
 * something they read rather than something they discover by losing a label.
 *
 * Element 0 is therefore never written by a move, which is why nothing calls
 * `normalizeFirstRow` on the way out: the guard is what protects tier 1 here,
 * not a strip after the fact.
 */
export function moveTierRow(rows, index, direction) {
  if (!canMoveTierRow(rows, index, direction)) return rows;

  const target = index + direction;
  const next = [...rows];
  next[index] = { ...rows[target], threshold: rows[index].threshold };
  next[target] = { ...rows[index], threshold: rows[target].threshold };
  return next;
}

/** Remove a row. Row 1 is not removable - the bar always has a first goal. */
export function removeTierRow(rows, index) {
  if (index <= 0 || index >= rows.length) return rows;
  return normalizeFirstRow(rows.filter((_, position) => position !== index));
}

/**
 * Client-side mirror of `ShippingBarService._validate_tiers` (S-4).
 *
 * The server is still the authority - this only spares the merchant a round
 * trip and a 400 they cannot act on. Ordering problems are attached to the
 * offending row so the message points at the field to fix; `formError` carries
 * the list-level ones only.
 */
export function validateTiers(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const rowErrors = list.map(() => ({}));
  let formError = null;

  if (list.length > MAX_TIERS) {
    formError = `A bar can have at most ${MAX_TIERS} tiers.`;
  }

  let previous = null;
  list.forEach((row, index) => {
    const position = index + 1;
    const threshold = toThresholdNumber(row.threshold);

    if (threshold === null || threshold < 0) {
      rowErrors[index].threshold = 'Enter a cart amount of 0 or more.';
    } else if (previous !== null && threshold <= previous) {
      rowErrors[index].threshold = `Tier ${position} must be above the tier before it (${previous}). Thresholds have to increase, and no two tiers can share one.`;
    }

    if (threshold !== null && threshold >= 0) {
      previous = previous === null || threshold > previous ? threshold : previous;
    }

    if ((row.reward_label || '').length > MAX_REWARD_LABEL_LENGTH) {
      rowErrors[index].reward_label = `Keep the reward label to ${MAX_REWARD_LABEL_LENGTH} characters or fewer.`;
    }

    if ((row.message || '').length > MAX_TIER_MESSAGE_LENGTH) {
      rowErrors[index].message = `Keep the message to ${MAX_TIER_MESSAGE_LENGTH} characters or fewer.`;
    }
  });

  const hasRowError = rowErrors.some((entry) => Object.keys(entry).length > 0);

  return { isValid: !formError && !hasRowError, formError, rowErrors };
}

/**
 * Build the `tiers` value for a save. The caller decides whether to send it at
 * all: a bar with one row keeps sending `free_shipping_threshold` /
 * `progress_message` and no `tiers` key, so its payload is unchanged (MT-5).
 */
export function toPayloadTiers(rows) {
  return rows.map((row, index) => ({
    id: row.id ?? null,
    threshold: toThresholdNumber(row.threshold),
    // Tier 1 is the bar's own columns, so it is always plain free shipping
    // with no label of its own - see tier_list() on the model.
    reward_type: index === 0 ? 'free_shipping' : row.reward_type || 'free_shipping',
    reward_label: index === 0 ? null : (row.reward_label || '').trim() || null,
    message: index === 0 ? row.message || '' : (row.message || '').trim() || null,
    is_enabled: row.is_enabled !== false,
  }));
}

/**
 * Build the list handed to `BarPreview`, mirroring
 * `ShippingBarService._renderable_tiers`: disabled rows are dropped, one tier
 * or fewer collapses to [] (empty means "single-threshold bar" to every
 * consumer), and tier 1 picks up the reward label the storefront payload gives
 * it. Without that last step the preview's `${earned}` omits tier 1 where the
 * real bar names it.
 */
export function toRenderableTiers(rows) {
  const visible = (Array.isArray(rows) ? rows : []).filter(
    (row) => row.is_enabled !== false
  );
  if (visible.length <= 1) return [];

  return visible.map((row, index) => ({
    threshold: toThresholdNumber(row.threshold) ?? 0,
    reward_type: row.reward_type || 'free_shipping',
    reward_label:
      index === 0
        ? (row.reward_label || '').trim() || TIER_ONE_REWARD_LABEL
        : (row.reward_label || '').trim(),
    message: row.message || '',
  }));
}

function TierList({ tiers, onChange, isPremium = false, currencyCode = 'USD' }) {
  const rows = Array.isArray(tiers) && tiers.length > 0 ? tiers : tierRowsFromBar({});
  const validation = useMemo(() => validateTiers(rows), [rows]);

  const updateRow = useCallback(
    (index, field, value) => {
      onChange(rows.map((row, position) => (position === index ? { ...row, [field]: value } : row)));
    },
    [rows, onChange]
  );

  const handleMove = useCallback(
    (index, direction) => onChange(moveTierRow(rows, index, direction)),
    [rows, onChange]
  );

  const handleRemove = useCallback(
    (index) => onChange(removeTierRow(rows, index)),
    [rows, onChange]
  );

  const handleAdd = useCallback(
    () => onChange([...rows, createTierRow(rows)]),
    [rows, onChange]
  );

  const renderRow = (row, index) => {
    const position = index + 1;
    const isFirst = index === 0;
    const rowErrors = validation.rowErrors[index] || {};

    const canMoveUp = canMoveTierRow(rows, index, -1);
    const canMoveDown = canMoveTierRow(rows, index, 1);

    // Two reasons an arrow is off, and only one of them needs explaining. At
    // the ends of the list it explains itself; blocked by the position-1 pin
    // it does not, and that is the click a merchant makes when they want the
    // gift to be the cheapest goal (R10).
    const upBlockedByPin = !canMoveUp && index > 0;
    const downBlockedByPin = !canMoveDown && index < rows.length - 1;

    const withPinReason = (blocked, control) =>
      blocked ? <Tooltip content={TIER_ONE_PINNED_REASON}>{control}</Tooltip> : control;

    return (
      <Box
        key={`tier-row-${index}`}
        background="bg-surface-secondary"
        padding="300"
        borderRadius="200"
      >
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="200" blockAlign="center">
              <Text variant="headingSm" as="h3">{`Tier ${position}`}</Text>
              {isFirst && <Badge>{TIER_ONE_REWARD_LABEL}</Badge>}
            </InlineStack>

            {rows.length > 1 && (
              <InlineStack gap="100">
                {withPinReason(
                  upBlockedByPin,
                  <Button
                    icon={ArrowUpIcon}
                    variant="tertiary"
                    accessibilityLabel={`Move tier ${position} up`}
                    disabled={!canMoveUp}
                    onClick={() => handleMove(index, -1)}
                  />
                )}
                {withPinReason(
                  downBlockedByPin,
                  <Button
                    icon={ArrowDownIcon}
                    variant="tertiary"
                    accessibilityLabel={`Move tier ${position} down`}
                    disabled={!canMoveDown}
                    onClick={() => handleMove(index, 1)}
                  />
                )}
                {!isFirst && (
                  <Button
                    icon={DeleteIcon}
                    variant="tertiary"
                    tone="critical"
                    accessibilityLabel={`Remove tier ${position}`}
                    onClick={() => handleRemove(index)}
                  />
                )}
              </InlineStack>
            )}
          </InlineStack>

          {/* A text input, not `type="number"`: a number input reports an
              in-progress decimal like "10." as the empty string, which wipes
              the field under the merchant as they type the point. Validation
              below catches anything that is not a number. */}
          <TextField
            label={`Tier ${position} threshold`}
            inputMode="decimal"
            autoComplete="off"
            prefix={currencyCode}
            value={row.threshold == null ? '' : String(row.threshold)}
            onChange={(value) => updateRow(index, 'threshold', value)}
            error={rowErrors.threshold}
            helpText={
              isFirst
                ? `Cart value required for this goal, in your store currency (${currencyCode}).`
                : undefined
            }
          />

          {isFirst ? (
            <Text variant="bodySm" tone="subdued">
              Tier 1 is your bar&apos;s main goal and always advertises free shipping, so it
              stays your cheapest goal and carries no reward label of its own. Add a tier
              below to promise a gift or a discount further up the ladder - those tiers
              reorder freely among themselves.
            </Text>
          ) : (
            <>
              <Select
                label={`Tier ${position} reward type`}
                options={REWARD_TYPE_OPTIONS}
                value={row.reward_type || 'free_shipping'}
                onChange={(value) => updateRow(index, 'reward_type', value)}
              />

              <TextField
                label={`Tier ${position} reward label`}
                value={row.reward_label || ''}
                onChange={(value) => updateRow(index, 'reward_label', value)}
                maxLength={MAX_REWARD_LABEL_LENGTH}
                showCharacterCount
                error={rowErrors.reward_label}
                placeholder="Free tote bag"
                helpText={`What ${PLACEHOLDER_EARNED} calls this reward once the shopper has cleared it. Leave it blank to keep this tier out of that list.`}
              />
            </>
          )}

          <TextField
            label={`Tier ${position} message`}
            value={row.message || ''}
            onChange={(value) => updateRow(index, 'message', value)}
            multiline={2}
            maxLength={MAX_TIER_MESSAGE_LENGTH}
            error={rowErrors.message}
            helpText={
              isFirst
                ? 'Shown while the cart is below this threshold. This is your bar’s progress message.'
                : `Shown while the shopper is working toward this tier. ${PLACEHOLDER_REMAINING} counts down to it.`
            }
          />
        </BlockStack>
      </Box>
    );
  };

  return (
    <BlockStack gap="400">
      {renderRow(rows[0], 0)}

      <PremiumFeatureLock isPremium={isPremium} feature="Multi-tier Goals">
        <BlockStack gap="400">
          {rows.slice(1).map((row, index) => renderRow(row, index + 1))}

          <InlineStack align="start" gap="200" blockAlign="center">
            <Button
              icon={PlusIcon}
              onClick={handleAdd}
              disabled={rows.length >= MAX_TIERS}
            >
              Add tier
            </Button>
            {rows.length >= MAX_TIERS && (
              <Text variant="bodySm" tone="subdued">
                {`A bar can have at most ${MAX_TIERS} tiers.`}
              </Text>
            )}
          </InlineStack>
        </BlockStack>
      </PremiumFeatureLock>

      {validation.formError && (
        <Banner tone="critical">
          <p>{validation.formError}</p>
        </Banner>
      )}
    </BlockStack>
  );
}

export default TierList;
